import type { FC } from "react"
import React, { useEffect } from "react"
import type { StackProps } from "@chakra-ui/react"
import {
  Spacer,
  Divider,
  Skeleton,
  Stack,
  Flex,
  Box,
  Text,
} from "@chakra-ui/react"
import invariant from "tiny-invariant"

import { useMultichainAccount } from "web3/hooks/useMultichainAccount"
import { DATE_FORMATS } from "common/constants/date"
import { useDevice } from "common/hooks/useDevice"
import { getProposalIdLabel } from "proposal/helpers/id"
import type {
  AccountId,
  ProposalHeaderQuery,
  Governor,
  Proposal,
  VoteStats,
  Account,
} from "query/graphql"
import { useProposalAccountVoteQuery, ProposalStatus } from "query/graphql"
import ProposalStatusTag from "proposal/components/ProposalStatusTag"
import ProposalOnChainCTA from "proposal/components/ProposalOnChainCTA"
import ProposalVoteOnboarding from "proposal/components/ProposalVoteOnboarding"
import ProposalVoteModal from "proposal/components/ProposalVoteModal"
import { useDescriptionException } from "proposal/hooks/useDescriptionException"
import AddressInfo from "common/components/AddressInfo"
import { getDateFromTimestamp, getTimestampFromIetf } from "common/helpers/date"
import CardContainer from "common/components/CardContainer"
import { useAddressEligibleFreeVoting } from "voting/hooks/useAddressElligibleFreeVoting"
import VotingEligibleMessage from "voting/components/VotingEligibleMessage"
import CopyButton from "common/components/CopyButton"
import { useDescriptionExceptions } from "proposal/hooks/useDescriptionExceptions"
import { _getDescriptionException } from "proposal/helpers/description"
import { useAddress } from "web3/hooks/useAddress"

type Props = {
  proposalId: string
  governorId: AccountId
  governorName: string
  slug: string
  isActive: boolean
  isMultiChain?: boolean
  isMultiGov?: boolean
  data: ProposalHeaderQuery | undefined
  isLoading: boolean
}

const ProposalHeader: FC<Props & StackProps> = ({
  proposalId,
  governorId,
  governorName,
  slug,
  isActive,
  isMultiChain = false,
  isMultiGov = false,
  data,
  isLoading,
  ...stackProps
}) => {
  const { onLargeDevice, onLittleDevice } = useDevice()
  const proposalLabelId = getProposalIdLabel(
    data?.proposal?.onchainId ?? proposalId,
  )

  const { descriptionExceptions } = useDescriptionExceptions()

  if (isLoading) {
    return (
      <CardContainer>
        <Stack align="flex-start" as="section" spacing={2} {...stackProps}>
          <Stack isInline align="center" spacing={1}>
            <Skeleton h={5} w={20} />
          </Stack>
          <Flex align="center" justify="space-between" w="full">
            <Skeleton h="2.235rem" w={40} />
            <Skeleton h={10} w={32} />
          </Flex>
          <Stack isInline align="center" spacing={2}>
            <Skeleton h={6} w={32} />
          </Stack>
        </Stack>
      </CardContainer>
    )
  }

  if (!data) return null

  const { proposal } = data
  const { governor, createdAt, onchainId, creator } = proposal

  invariant(governor, "governor is required")

  const { token, timelockId } = governor
  const { decimals } = token

  const timestamp = getTimestampFromIetf(createdAt)
  const date = getDateFromTimestamp(timestamp)
  const createdDateLabel = date.format(DATE_FORMATS.monthThYear)

  const { descriptionException } = _getDescriptionException({
    descriptionExceptions,
    governanceId: governorId,
    proposalId: data?.proposal.id ?? "",
    proposalOnchainId: data?.proposal.onchainId ?? "",
  })

  const hideStatusLabel = descriptionException?.disabled === true

  const getVotableChainIds = () => {
    const proposalVotableChains = proposal.votableChains
      ? [...proposal.votableChains]
      : []
    const proposalChainId = proposal.chainId ? [proposal.chainId] : []

    return proposalVotableChains.concat(proposalChainId)
  }

  const votableChainIds = getVotableChainIds()

  return (
    <>
      <CardContainer isTableView pb={2}>
        <Stack spacing={3} w="full">
          <Stack
            align="flex-start"
            as="section"
            mt={{ base: 2, lg: 2 }}
            p={{ base: 3, lg: 0 }}
            spacing={{ base: 1, sm: 0 }}
            w="full"
            {...stackProps}
          >
            <Stack
              isInline
              align="center"
              justify={{ base: "space-between", lg: "flex-start" }}
              mb={{ base: 0, lg: 2 }}
              pt={{ base: 0, sm: 4 }}
              px={{ base: 0, sm: 6 }}
              w={{ base: "full", lg: "auto" }}
            >
              {proposal.status && !hideStatusLabel ? (
                <ProposalStatusTag
                  decimals={decimals}
                  governor={governor}
                  quorum={proposal.quorum ?? governor.quorum}
                  status={proposal.status}
                  timelockId={timelockId}
                  voteStats={proposal?.voteStats as VoteStats[]}
                />
              ) : null}
            </Stack>
            <Stack
              direction={{ base: "column", lg: "row" }}
              mb={{ base: 3, lg: 0 }}
              pt={{ base: 2, sm: 1 }}
              px={{ base: 1, sm: 6 }}
              spacing={{ base: 1, lg: 6 }}
              width="100%"
            >
              <Title
                governanceId={governorId}
                proposal={proposal as Proposal}
              />

              <Stack spacing={12} w={{ base: "full", lg: 80 }}>
                <ProposalOnChainCTA
                  display={{ base: "none", lg: "flex" }}
                  proposal={proposal as Proposal}
                  slug={slug}
                />
              </Stack>
            </Stack>
            <Divider py={{ base: 1, sm: 2 }} />
            <Stack
              align={{ base: "start", lg: "center" }}
              direction={{ base: "column", lg: "row" }}
              pt={{ base: 0, sm: 1 }}
              px={{ base: 1, sm: 6 }}
              spacing={{ base: 1, sm: 3 }}
              width="100%"
            >
              <AddressInfo
                account={creator as Account}
                label="by"
                slug={slug}
              />
              <Box
                bg="gray.500"
                display={onLargeDevice}
                h={1}
                rounded="full"
                w={1}
              />
              <Stack align="center" direction="row" spacing={1}>
                <Text
                  color="gray.500"
                  display={onLargeDevice}
                  textStyle="body.bold.md"
                  w="max-content"
                >
                  ID {proposalLabelId}
                </Text>
                <Text
                  color="gray.500"
                  display={onLittleDevice}
                  textStyle="body.bold.sm"
                  w="max-content"
                >
                  ID {proposalLabelId}
                </Text>
                <CopyButton
                  size={8}
                  tooltipLabel="proposal id"
                  value={data?.proposal?.onchainId ?? proposalId}
                />

                {isMultiGov ? (
                  <>
                    <Box
                      bg="gray.500"
                      display={{ base: "none", md: "flex" }}
                      h={1}
                      mr={2}
                      rounded="full"
                      w={1}
                    />
                    <Text
                      color="gray.500"
                      display={onLargeDevice}
                      textStyle="body.bold.md"
                    >
                      {governor?.name}
                    </Text>
                    <Text
                      color="gray.500"
                      display={onLittleDevice}
                      textStyle="body.bold.sm"
                    >
                      {governor?.name}
                    </Text>
                  </>
                ) : null}
              </Stack>

              <Box
                bg="gray.500"
                display={onLargeDevice}
                h={1}
                rounded="full"
                w={1}
              />
              <Text
                color="gray.500"
                display={onLargeDevice}
                textStyle="body.bold.md"
                w="max-content"
              >
                Proposed on: {createdDateLabel}
              </Text>
              <Text
                color="gray.500"
                display={onLittleDevice}
                textStyle="body.bold.sm"
                w="max-content"
              >
                Proposed on: {createdDateLabel}
              </Text>
              <Spacer />
              <VotingEligible
                governanceId={governorId}
                proposal={proposal as Proposal}
              />
            </Stack>
          </Stack>
        </Stack>
      </CardContainer>
      <ProposalVoteOnboarding governance={governor as Governor} />
      <ProposalVoteModal
        governance={governor as Governor}
        governanceName={governorName}
        isActive={isActive}
        isMultiChain={isMultiChain}
        onchainId={onchainId}
        proposalId={proposal.id}
        votableChainIds={votableChainIds}
      />
    </>
  )
}

export default ProposalHeader

type TitleProps = {
  proposal: Proposal
  governanceId: AccountId
}

const Title: FC<TitleProps> = ({ proposal, governanceId }) => {
  const { title } = useDescriptionException({
    governanceId,
    proposal,
  })

  return (
    <Flex flex={1}>
      <Text as="h3" fontWeight="bold" textStyle="h3" wordBreak="break-word">
        {title}
      </Text>
    </Flex>
  )
}

const VotingEligible = ({
  governanceId,
  proposal,
}: {
  governanceId: string
  proposal: Proposal
}) => {
  const voterAddress = useAddress()
  const { multichainAccount } = useMultichainAccount()
  const { type: accountType } = multichainAccount

  const { data, refetch } = useProposalAccountVoteQuery(
    {
      input: {
        filters: {
          proposalId: proposal.id,
          voter: voterAddress ?? undefined,
        },
      },
    },
    { enabled: Boolean(voterAddress) },
  )

  useEffect(() => {
    if (voterAddress) {
      refetch() // This will re-execute the query
    }
  }, [voterAddress, refetch])

  const hasVoted = data?.votes?.nodes.length ?? false

  const { id: proposalId } = proposal

  const eligibleData = useAddressEligibleFreeVoting({
    governanceId,
    voterAddress,
    proposalId,
    accountType,
    enabled:
      (proposal.status === ProposalStatus.Active ||
        proposal.status === ProposalStatus.Extended) &&
      Boolean(hasVoted) === false,
  })

  if (!eligibleData?.sponsorshipSupported) {
    return null
  }

  if (hasVoted) {
    return null
  }

  return (
    <VotingEligibleMessage
      addressEligible={eligibleData.addressEligible}
      displayLearnMore={false}
    />
  )
}
